import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { HotkeysService } from '../../core/a11y/hotkeys.service';
import type { KeyCode } from '../../core/keycode';
import { KeySymbol, ModifierKey } from '../../core/keycode';
import { RtPlatform } from '../../core/platform/platform';

export enum Os {
  Win = 'win',
  Osx = 'osx',
}

export type HotkeyConfigs = Record<Os.Win | Os.Osx, HotkeyConfig>;

export type HotkeyConfig = {
  keys: KeyCode[];
  modifiers: ModifierKey[];
  noSymbol?: boolean;
};

@Component({
  selector: 'rt-hotkey',
  template: '<div class="hotkey">{{ label }}</div>',
  styleUrls: ['./hotkey.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class HotkeyComponent implements OnInit {
  @Input() public config: HotkeyConfigs;
  @Input() public inputFocusLock: boolean = true;

  @Output() public hotkeyFired: EventEmitter<void> = new EventEmitter();

  public currentPlatform: string;
  public currentConfig: HotkeyConfig;
  public label: string = '';

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private hotkeysService: HotkeysService,
    private rtPlatform: RtPlatform,
  ) {
    this.currentPlatform = this.rtPlatform.currentPlatform.toString();
  }

  public ngOnInit(): void {
    this.currentConfig = this.config[this.returnPlatform()];
    this.currentConfig.modifiers = <ModifierKey[]>[
      ...this.removeDuplicates(this.currentConfig.modifiers),
    ];
    this.convertKeysToLabel();

    this.hotkeysService
      .shortcut([...this.currentConfig.keys, ...this.currentConfig.modifiers], this.inputFocusLock)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.hotkeyFired.emit();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private removeDuplicates(keys: Array<KeyCode | ModifierKey>): Array<KeyCode | ModifierKey> {
    return [...new Set(keys)];
  }

  private convertCmdToSymbol(key: ModifierKey): ModifierKey | KeySymbol {
    return key === ModifierKey.Command && !this.currentConfig.noSymbol ? KeySymbol.Command : key;
  }

  private convertKeysToLabel() {
    this.currentConfig.modifiers.forEach(el => {
      const separator = this.label || el !== ModifierKey.Command ? ' + ' : ' ';
      this.label = `${this.label}${this.convertCmdToSymbol(el)}${separator}`;
    });

    this.currentConfig.keys.forEach((el, index, array) => {
      this.label = `${this.label}${String.fromCharCode(el)}${
        index >= array.length - 1 ? '' : ' + '
      }`;
    });
  }

  private returnPlatform() {
    return this.currentPlatform.indexOf(Os.Osx) === -1 ? Os.Win : Os.Osx;
  }
}
